import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, RotateCw, Link as LinkIcon, ExternalLink } from "lucide-react";

/**
 * Coach-only Team-Invite Link card. ONE evergreen link per team.
 * Coach can copy and share with any number of recruits, or rotate the token to
 * invalidate the old link and issue a fresh one.
 */
export default function TeamInviteLinkCard() {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = token ? `${origin}/team-invite/${token}` : "";

  const load = async () => {
    try {
      const { data } = await api.get("/teams/me/invite-link");
      setToken(data.token);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); toast.success("Team invite link copied"); }
    catch (_) { toast.error("Couldn't copy automatically — long-press to copy"); }
  };

  const regenerate = async () => {
    if (!window.confirm("Rotate the link? The old URL will stop working immediately.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/teams/me/invite-link/regenerate");
      setToken(data.token);
      toast.success("New team invite link generated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl ink-card p-6" data-testid="team-invite-link-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-lg text-white flex items-center gap-2">
            <LinkIcon className="h-5 w-5" style={{ color: "var(--brand-secondary)" }} />
            Team Invite Link
          </h2>
          <p className="text-sm text-white/50 mt-1">One link, share with as many recruits as you want. Anyone who opens it creates a player account inside this team.</p>
        </div>
        <a
          href={url || "#"}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-[var(--brand-secondary)] hover:underline inline-flex items-center gap-1"
          data-testid="team-invite-preview"
        >
          Preview <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="mt-5 flex items-center gap-2">
        <Input
          readOnly
          value={url || "Generating link…"}
          className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white font-mono-num text-sm flex-1"
          data-testid="team-invite-url"
          onFocus={(e) => e.target.select()}
        />
        <Button onClick={copy} className="text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="team-invite-copy">
          <Copy className="h-4 w-4 mr-2" /> Copy
        </Button>
        <Button variant="outline" disabled={busy} onClick={regenerate} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="team-invite-regenerate" title="Rotate this link — old URL stops working">
          <RotateCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <div className="mt-3 text-xs text-white/40">
        Tip: post this link in your team's group chat or print it as a QR code in the locker room.
      </div>
    </section>
  );
}
